import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  LayoutDashboard,
  Clock,
  Car,
  Users,
  Receipt,
  Settings,
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  HelpCircle
} from 'lucide-react';

// Tour steps configuration
const TOUR_STEPS = [
  {
    id: 'welcome',
    type: 'modal',
    icon: Sparkles,
    titleKey: 'onboarding.welcomeTitle',
    descriptionKey: 'onboarding.welcomeDescription',
  },
  {
    id: 'dashboard',
    type: 'highlight',
    path: '/dashboard',
    targetSelector: '[data-testid="dashboard-page"]',
    icon: LayoutDashboard,
    titleKey: 'onboarding.dashboardTitle',
    descriptionKey: 'onboarding.dashboardDescription',
    position: 'bottom-right',
  },
  {
    id: 'timer',
    type: 'highlight',
    path: '/dashboard',
    targetSelector: '[data-testid="nav-timer"]',
    icon: Clock,
    titleKey: 'onboarding.timerTitle',
    descriptionKey: 'onboarding.timerDescription',
    position: 'right',
  },
  {
    id: 'registrations',
    type: 'highlight',
    path: '/dashboard',
    targetSelector: '[data-testid="nav-registraties"]',
    icon: Car,
    titleKey: 'onboarding.registrationsTitle',
    descriptionKey: 'onboarding.registrationsDescription',
    position: 'right',
  },
  {
    id: 'clients',
    type: 'highlight',
    path: '/dashboard',
    targetSelector: '[data-testid="nav-klanten"]',
    icon: Users,
    titleKey: 'onboarding.clientsTitle',
    descriptionKey: 'onboarding.clientsDescription',
    position: 'right',
  },
  {
    id: 'invoices',
    type: 'highlight',
    path: '/dashboard',
    targetSelector: '[data-testid="nav-facturen"]',
    icon: Receipt,
    titleKey: 'onboarding.invoicesTitle',
    descriptionKey: 'onboarding.invoicesDescription',
    position: 'right',
  },
  {
    id: 'settings',
    type: 'highlight',
    path: '/dashboard',
    targetSelector: '[data-testid="nav-instellingen"]',
    icon: Settings,
    titleKey: 'onboarding.settingsTitle',
    descriptionKey: 'onboarding.settingsDescription',
    position: 'right',
  },
  {
    id: 'complete',
    type: 'modal',
    icon: Sparkles,
    titleKey: 'onboarding.completeTitle',
    descriptionKey: 'onboarding.completeDescription',
  },
];

// Tooltip component for highlighting elements
function Tooltip({ step, onNext, onPrev, onSkip, currentIndex, totalSteps, t }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [targetRect, setTargetRect] = useState(null);
  const Icon = step.icon;

  useEffect(() => {
    const updatePosition = () => {
      const target = document.querySelector(step.targetSelector);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);

        let top, left;
        const tooltipWidth = 320;
        const tooltipHeight = 200;
        const padding = 16;

        switch (step.position) {
          case 'right':
            top = rect.top + rect.height / 2 - tooltipHeight / 2;
            left = rect.right + padding;
            break;
          case 'left':
            top = rect.top + rect.height / 2 - tooltipHeight / 2;
            left = rect.left - tooltipWidth - padding;
            break;
          case 'bottom':
            top = rect.bottom + padding;
            left = rect.left + rect.width / 2 - tooltipWidth / 2;
            break;
          case 'bottom-right':
            top = rect.top + padding;
            left = rect.left + padding;
            break;
          default:
            top = rect.bottom + padding;
            left = rect.left;
        }

        // Keep tooltip in viewport
        top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
        left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));

        setPosition({ top, left });
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [step]);

  if (!targetRect) return null;

  return (
    <>
      {/* Overlay with hole for target element */}
      <div className="fixed inset-0 z-[9998]">
        <svg className="w-full h-full">
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={targetRect.left - 4}
                y={targetRect.top - 4}
                width={targetRect.width + 8}
                height={targetRect.height + 8}
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.5)"
            mask="url(#tour-mask)"
          />
        </svg>
      </div>

      {/* Highlight border around target */}
      <div
        className="fixed z-[9999] pointer-events-none rounded-lg ring-4 ring-cyan-500 ring-offset-2"
        style={{
          top: targetRect.top - 4,
          left: targetRect.left - 4,
          width: targetRect.width + 8,
          height: targetRect.height + 8,
        }}
      />

      {/* Tooltip card */}
      <div
        className="fixed z-[10000] w-80 bg-card border border-border rounded-xl shadow-2xl animate-fade-in"
        style={{ top: position.top, left: position.left }}
        data-testid="onboarding-tooltip"
      >
        <div className="p-4">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/50 rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{t(step.titleKey)}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t(step.descriptionKey)}</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-border">
            <div className="flex items-center gap-1">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === currentIndex ? 'bg-cyan-500' : 'bg-muted'
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={onSkip}
                className="text-muted-foreground"
                data-testid="tour-skip-btn"
              >
                {t('onboarding.skip')}
              </Button>
              {currentIndex > 1 && (
                <Button variant="outline" size="sm" onClick={onPrev} data-testid="tour-prev-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="sm"
                onClick={onNext}
                className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                data-testid="tour-next-btn"
              >
                {t('onboarding.next')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Welcome/Complete modal component
function WelcomeModal({ step, onNext, onSkip, isComplete, t }) {
  const Icon = step.icon;

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md"
        hideCloseButton
        data-testid={isComplete ? 'onboarding-complete-modal' : 'onboarding-welcome-modal'}
      >
        <div className="flex flex-col items-center text-center p-4">
          <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-cyan-500/25">
            <Icon className="w-8 h-8 text-white" />
          </div>

          <h2 className="text-2xl font-bold text-foreground mb-2">{t(step.titleKey)}</h2>
          <p className="text-muted-foreground mb-6">{t(step.descriptionKey)}</p>

          <div className="flex gap-3 w-full">
            {!isComplete && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={onSkip}
                data-testid="tour-skip-btn"
              >
                {t('onboarding.skipTour')}
              </Button>
            )}
            <Button
              className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
              onClick={onNext}
              data-testid="tour-start-btn"
            >
              {isComplete ? t('onboarding.finish') : t('onboarding.startTour')}
              {!isComplete && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Main OnboardingTour component
export default function OnboardingTour({ isActive, onComplete }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const currentStep = TOUR_STEPS[currentStepIndex];

  const completeTour = useCallback(() => {
    localStorage.setItem('urenbuddy_tour_completed', 'true');
    onComplete();
  }, [onComplete]);

  const handleNext = useCallback(() => {
    if (currentStepIndex === TOUR_STEPS.length - 1) {
      completeTour();
    } else {
      const nextStep = TOUR_STEPS[currentStepIndex + 1];
      if (nextStep.path && nextStep.path !== location.pathname) {
        navigate(nextStep.path);
      }
      setCurrentStepIndex((prev) => prev + 1);
    }
  }, [currentStepIndex, completeTour, location.pathname, navigate]);

  const handlePrev = useCallback(() => {
    if (currentStepIndex > 0) {
      const prevStep = TOUR_STEPS[currentStepIndex - 1];
      if (prevStep.path && prevStep.path !== location.pathname) {
        navigate(prevStep.path);
      }
      setCurrentStepIndex((prev) => prev - 1);
    }
  }, [currentStepIndex, location.pathname, navigate]);

  const handleSkip = useCallback(() => {
    completeTour();
  }, [completeTour]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isActive) return;
      if (e.key === 'Escape') {
        handleSkip();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        handlePrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, handleNext, handlePrev, handleSkip]);

  if (!isActive) return null;

  // Render modal steps
  if (currentStep.type === 'modal') {
    return (
      <WelcomeModal
        step={currentStep}
        onNext={handleNext}
        onSkip={handleSkip}
        isComplete={currentStep.id === 'complete'}
        t={t}
      />
    );
  }

  // Render highlight/tooltip steps
  return (
    <Tooltip
      step={currentStep}
      onNext={handleNext}
      onPrev={handlePrev}
      onSkip={handleSkip}
      currentIndex={currentStepIndex}
      totalSteps={TOUR_STEPS.length}
      t={t}
    />
  );
}

// Help button component to restart tour
export function HelpButton({ onClick }) {
  const { t } = useTranslation();

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={onClick}
      className="rounded-full text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
      title={t('onboarding.restartTour')}
      data-testid="help-tour-btn"
    >
      <HelpCircle className="w-5 h-5" />
    </Button>
  );
}

// Hook to manage tour state
export function useOnboardingTour() {
  const [isTourActive, setIsTourActive] = useState(false);
  const [hasCheckedTour, setHasCheckedTour] = useState(false);

  // Check if this is a new user who hasn't completed the tour
  const checkAndStartTour = useCallback((isNewUser = false) => {
    const tourCompleted = localStorage.getItem('urenbuddy_tour_completed');
    if (isNewUser || !tourCompleted) {
      setIsTourActive(true);
    }
    setHasCheckedTour(true);
  }, []);

  const startTour = useCallback(() => {
    setIsTourActive(true);
  }, []);

  const completeTour = useCallback(() => {
    setIsTourActive(false);
  }, []);

  return {
    isTourActive,
    hasCheckedTour,
    checkAndStartTour,
    startTour,
    completeTour,
  };
}
